import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrencyColumn } from 'src/feature/data-grid/column/use-currency-column';
import { useDatetimeColumn } from 'src/feature/data-grid/column/use-datetime-column';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { APP_PATH } from 'src/config/app-paths';
import { useTextColumn } from 'src/feature/data-grid/column/use-text-column';
import { useCopyColumn } from 'src/feature/data-grid/column/use-copy-column';
import { usePseudoColumn } from 'src/feature/data-grid/column/use-pseudo-column';
import { InvoiceStatusEnum } from '@delmonicos/shared-sdk/libs/emsp/src/libs/shared/src/invoice-service/enum/invoice-status.enum';
import { InvoiceModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/invoices-service-lib/src/wrapper/outputs/invoice.model';

export function useInvoiceColumn(): GridColDef[] {
  const { t } = useTranslation();

  const currencyColumn = useCurrencyColumn();
  const datetimeColumn = useDatetimeColumn();
  const textColumn = useTextColumn();
  const copyColumn = useCopyColumn();
  const pseudoColumn = usePseudoColumn();

  const invoiceTranslations: Record<keyof InvoiceModel, string> = t(
    'model.invoice-v1',
    {
      returnObjects: true,
    },
  );

  return useMemo(
    () => [
      {
        ...copyColumn,
        field: 'invoiceId',
        headerName: invoiceTranslations.id,
        width: 180,
      },
      {
        ...pseudoColumn,
        field: 'customerPseudo',
        headerName: invoiceTranslations.emspCustomer,
      },
      {
        ...textColumn,
        field: 'status',
        headerName: invoiceTranslations.status,
        width: 200,

        valueFormatter: (
          params: GridValueFormatterParams<InvoiceStatusEnum>,
        ): string =>
          t(`model.enum.invoice-status.${params.value}`, params.value),
      },
      {
        ...datetimeColumn,
        field: 'createdAt',
        headerName: invoiceTranslations.createdAt,
      },
      {
        ...currencyColumn,
        field: 'totalTTC',
        headerName: invoiceTranslations.totalTTC,
      },
      {
        field: 'actions',
        type: 'actions',
        width: 80,

        getActions: (params: GridRowParams<InvoiceModel>) => [
          params.row.id && (
            <GridActionsCellItem
              key="link-to-html"
              component={RouterLink}
              target="_blank"
              label={t('invoice-v1.data-grid.cta.preview-pdf')}
              icon={<OpenInNewIcon />}
              to={generatePath(APP_PATH.INVOICE_HTML_V1, {
                invoiceId: params.row.id,
              })}
            />
          ),
        ],
      },
    ],
    [
      t,
      invoiceTranslations,
      currencyColumn,
      datetimeColumn,
      textColumn,
      copyColumn,
      pseudoColumn,
    ],
  );
}
